import React from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  Dimensions,
  Linking,
  ListRenderItem,
  TouchableOpacity,
  FlatList
} from "react-native"
import { observer } from "mobx-react-lite"
import { StackScreenProps } from "@react-navigation/stack"
import { Button, Screen, Text, Svg } from "@app/components"
import { TextProps } from "@app/components/text/text.props"
import { color, spacing } from "@app/theme"
import { Footer } from "@app/components/footer/footer"
import { Feather } from "@expo/vector-icons"
import { textPresets } from "@app/components/button/button.presets"
import { RootNavigatorParamList } from "@app/navigators/navigator-params"

type ListItem = {
  text: any
  preset: TextProps["preset"]
}

const SUPPORT_EMAIL = "kundtjanst@veckanse.com"

type ConsentScreenProps = StackScreenProps<RootNavigatorParamList, "Consent">
export const ConsentScreen = observer((props: ConsentScreenProps) => {
  const goHome = () =>
    props.navigation.navigate("Tabs", { screen: "Home", params: { screen: "Offers" } })

  const renderItem: ListRenderItem<ListItem> = ({ item }) => {
    switch (item.preset) {
      case "bold":
        return (
          <Text preset={item.preset} style={LIST_ITEM_BOLD}>
            {item.text}
          </Text>
        )
      default:
        return (
          <Text preset={item.preset} style={LIST_ITEM_DEFAULT}>
            {item.text}
          </Text>
        )
    }
  }

  const mailLink = (
    <TouchableOpacity onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}>
      <Text style={LINK} text={SUPPORT_EMAIL} />
    </TouchableOpacity>
  )

  return (
    <Screen style={ROOT} preset="scroll">
      <View style={PAGE_WRAPPER}>
        <View style={BACKGROUND_IMAGE}>
          <Svg preset="PinkBlob" />
        </View>
        <Button preset="back" onPress={goHome}>
          <Feather style={textPresets.back} name="chevron-left" />
          <Text style={textPresets.back} text="Tillbaka" />
        </Button>
        <Text
          style={HEADER_LARGE}
          preset="headerLarge"
          text="Samtycke – Klickspårning i nyhetsbrev"
        />
        <Text
          preset="default"
          text="Inhämtande av samtycke enligt LEK och GDPR"
        />
        <Text
          preset="default"
          text="CARTERO Digital Marketing AB | Org.nr 556857–4262 | Nybrokajen 7, 111 48 Stockholm"
        />
        <Text preset="default" text="Version 1.0 – 2026-06-08" />
        <FlatList
          data={[
            { preset: "bold", text: "1. Om samtycket" },
            {
              preset: "default",
              text: "Detta dokument inhämtar ditt samtycke till att vi behandlar personuppgifter i samband med klickspårning i våra nyhetsbrev, i enlighet med 9 kap. 28 § lag (2022:482) om elektronisk kommunikation (LEK) och art. 6.1 (a) allmänna dataskyddsförordningen (GDPR), samt beskriver ändamålet med denna behandling."
            },
            {
              preset: "default",
              text: "Samtycke krävs eftersom klickspårning innebär lagring av och/eller åtkomst till information i din terminal (e-postklient/webbläsare). Ditt samtycke är frivilligt och kan återkallas när som helst utan att detta påverkar lagligheten av den behandling som skett före återkallelsen."
            },
            { preset: "bold", text: "2. Vad som behandlas och varför" },
            { preset: "bold", text: "2.1 Personuppgiftsansvarig" },
            { preset: "default", text: "Organisation: CARTERO Digital Marketing AB" },
            { preset: "default", text: "Organisationsnummer: 556857–4262" },
            { preset: "default", text: "Adress: Nybrokajen 7, 111 48 Stockholm" },
            {
              preset: "default",
              text: (
                <>
                  E-post: {mailLink}
                </>
              )
            },
            { preset: "bold", text: "2.2 Uppgifter som behandlas" },
            {
              preset: "default",
              text: "Vid klickspårning i VeckansEs nyhetsbrev kan följande uppgifter samlas in och behandlas:"
            },
            { preset: "default", text: "• E-postadress" },
            { preset: "default", text: "• Länkar i nyhetsbrev som har klickats på" },
            { preset: "default", text: "• Eventuell tidpunkt för klick" },
            { preset: "default", text: "• Om och när nyhetsbrevet öppnades" },
            {
              preset: "default",
              text: "• Ungefärlig geografisk plats samt enhetstyp som använts"
            },
            { preset: "bold", text: "2.3 Ändamål med behandlingen" },
            { preset: "default", text: "Uppgifterna som behandlas är för följande ändamål:" },
            {
              preset: "default",
              text: "• Analysera vilket innehåll som är relevant för dig som prenumerant"
            },
            {
              preset: "default",
              text: "• Förbättra och anpassa framtida nyhetsbrevs innehåll, inkluderat erbjudanden, format och utskickstider"
            },
            {
              preset: "default",
              text: "• Ta fram aggregerad statistik om nyhetsbrevets genomslagskraft innefattat öppningsfrekvens, mottagande och uppföljande av enskilda erbjudanden"
            },
            {
              preset: "default",
              text: "• Säkerställa att VeckansEs kommunikation uppfyller prenumerantens behov och intressen"
            },
            {
              preset: "default",
              text: "• Skapa en bättre och mer relevant tjänst och tjänsteupplevelse för varje enskild prenumerant"
            },
            { preset: "bold", text: "2.4 Laglig grund för behandlingen" },
            {
              preset: "default",
              text: "Ifrågavarande behandling grundar sig uteslutande på ditt frivilliga, informerade och uttryckliga samtycke, i enlighet med art. 6.1 (a) GDPR och 9 kap. 28 § LEK."
            },
            { preset: "bold", text: "2.5 Lagringstid" },
            {
              preset: "default",
              text: "Dina uppgifter lagras så länge du är prenumerant på VeckansEs nyhetsbrev och upp till 30 dagar efter avslutande av prenumerationen. Vissa uppgifter kan komma att lagras längre, för det fall det finns krav på detta enligt lag."
            },
            { preset: "bold", text: "2.6 Mottagare och tredjelandsöverföring" },
            {
              preset: "default",
              text: "Personuppgifter behandlas i huvudsak inom EU/EES. Om uppgifter överförs till ett land utanför EU/EES, sker detta med stöd av lämpliga skyddsåtgärder enligt art. 46, t.ex. EU-kommissionens standardavtalsklausuler (SCC)."
            },
            { preset: "bold", text: "3. Dina rättigheter" },
            { preset: "default", text: "Du har rätt att när som helst:" },
            {
              preset: "default",
              text: "• Återkalla ditt samtycke (detta påverkar inte behandlingens laglighet dessförinnan)"
            },
            {
              preset: "default",
              text: "• Begära tillgång till de personuppgifter som vi behandlar om dig (registerutdrag)"
            },
            { preset: "default", text: "• Begära rättelse av felaktiga uppgifter" },
            {
              preset: "default",
              text: "• Begära radering av dina uppgifter (rätten att bli bortglömd)"
            },
            { preset: "default", text: "• Begära begränsning av behandlingen" },
            { preset: "default", text: "• Invända mot behandlingen" },
            { preset: "default", text: "• Begära dataportabilitet" },
            {
              preset: "default",
              text: (
                <>
                  Kontakt för utövande av rättigheter: {mailLink}
                </>
              )
            },
            {
              preset: "default",
              text: "Du har även rätt att inge klagomål avseende behandlingen till Integritetsskyddsmyndigheten (IMY), imy.se."
            },
            { preset: "default", text: "Version 1.0 – 2026-06-08" },
            {
              preset: "default",
              text: (
                <>
                  Frågor: {mailLink}
                </>
              )
            }
          ]}
          renderItem={renderItem}
        />
        <View></View>
      </View>
      <Footer />
    </Screen>
  )
})

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  minHeight: Dimensions.get("window").height
}

const LIST_ITEM_BOLD: ViewStyle = {
  marginTop: spacing[4],
  marginBottom: spacing[2]
}
const LIST_ITEM_DEFAULT: ViewStyle = {
  marginLeft: spacing[3],
  marginBottom: spacing[2]
}

const PAGE_WRAPPER: ViewStyle = {
  padding: 24,
  paddingBottom: 80,
  maxWidth: 840,
  minHeight: Dimensions.get("window").height,
  alignSelf: "center"
}

const BACKGROUND_IMAGE: ViewStyle = {
  height: 500,
  width: 580,
  position: "absolute",
  top: -100,
  left: -200,
  transform:
    Dimensions.get("window").width > 768
      ? [{ scale: 2 }, { rotate: "45deg" }]
      : [{ scale: 1 }, { rotate: "45deg" }],
  zIndex: -1
}

const HEADER_LARGE: TextStyle = {
  marginTop: spacing[5],
  marginBottom: spacing[5]
}

const LINK: TextStyle = {
  color: color.palette.pink3
}
